import { useEffect, useMemo, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import { nanoid } from 'nanoid';
import analyticsService from 'src/helpers/analytics.service';

/**
 * Custom hook to track analytics impression when an element comes into view.
 * @param {Object} params - Parameters for the hook.
 * @param {string|null} params.event - The analytics event to track when the element is in view, or null to skip tracking.
 * @param {Object} [params.eventPayload={}] - Optional payload to include with the analytics event.
 * @param {Object} [params.inViewConfig={}] - Optional configuration for the useInView hook.
 * @param {string} [params.impressionId] - Id to report the impression under. Supply one when it has to stay
 * stable across remounts; otherwise each mount gets its own.
 * @param {Function} [params.onTrack] - Optional callback invoked after the impression event is tracked.
 */
export function useAnalyticsImpression({
  event,
  eventPayload = {},
  inViewConfig = {},
  impressionId: providedImpressionId,
  onTrack,
}) {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.75, ...inViewConfig });

  const mountImpressionId = useMemo(() => nanoid(), []);
  const impressionId = providedImpressionId ?? mountImpressionId;
  const hasFired = useRef(false);

  useEffect(() => {
    if (inView && event && !hasFired.current) {
      hasFired.current = true;
      analyticsService.track(event, { ...eventPayload, impression_id: impressionId });
      onTrack?.();
    }
  }, [inView, event, eventPayload, impressionId, onTrack]);

  return { ref, impressionId };
}
